// DismissDialog.tsx — asks why a proposed cleanup is not worth doing.
//
// The reason is optional, so the dialog commits on an empty field exactly as
// the one-click Dismiss used to. It is asked for at all because it is what the
// next review of the directory is shown: "dismissed" stops a finding coming
// back word for word, but only the reason stops the same point coming back
// under a different title. Cancelling the dialog cancels the dismissal; it
// never sends one without the reason the person was about to type.
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { BsXLg } from 'react-icons/bs';
import Text from 'common/ui/Text';

// The server keeps at most this many characters of a reason.
const MAX_REASON = 1000;

export default function DismissDialog({ title, show, onDismiss, onCancel }: {
  // The finding's title, so the person can see which one they are answering.
  title: string;
  show: boolean;
  onDismiss: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  // A fresh field for every finding: a reason typed for one must never ride
  // along onto the next.
  useEffect(() => {
    if (show) setReason('');
  }, [show, title]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onDismiss(reason.trim());
  };

  return (
    <Modal show={show} onHide={onCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title className="h5 m-0">Dismiss Finding</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form id="dismiss-finding-form" onSubmit={submit}>
          <Form.Label className="d-block">{title}</Form.Label>
          <Text
            type="text"
            style={{ width: '100%' }}
            value={reason}
            maxLength={MAX_REASON}
            autoFocus
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this not worth doing? (optional)"
          />
          <Form.Text as="div" muted>
            The next review of this directory is told, so it does not raise the
            same point again in other words.
          </Form.Text>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="outline-secondary" type="submit" form="dismiss-finding-form">
          <span className="d-inline-flex align-items-center gap-1">
            <BsXLg />
            Dismiss
          </span>
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
